import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Calculator } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignaturePad = ({ onEnd }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const startDrawing = (e) => {
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#FACC15";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onEnd(canvasRef.current.toDataURL());
  };

  const clear = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onEnd("");
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return { offsetX: e.touches[0].clientX - rect.left, offsetY: e.touches[0].clientY - rect.top };
    }
    return { offsetX: e.nativeEvent.offsetX, offsetY: e.nativeEvent.offsetY };
  };

  return (
    <div className="border border-border_soft bg-main_bg rounded-md overflow-hidden relative shadow-inner">
      <canvas
        ref={canvasRef}
        width={600}
        height={150}
        className="w-full h-full touch-none cursor-crosshair"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button onClick={clear} type="button" className="absolute top-2 right-2 text-xs bg-card_bg border border-border_soft text-text_secondary px-2 py-1 rounded hover:text-text_primary transition-colors">Clear</button>
    </div>
  );
};

const PersonalDetails = ({ formData, handleChange, req }) => {
  const star = (k) => (req(k) ? " *" : "");
  return (
  <section>
    <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Personal Details</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">First Name *</label>
        <input required name="firstName" value={formData.firstName} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Middle Initial</label>
        <input name="middleName" value={formData.middleName} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Last Name *</label>
        <input required name="lastName" value={formData.lastName} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
    </div>
    <div className="mb-4">
      <label className="text-xs text-text_secondary mb-1 block">Address{star("address")}</label>
      <input required={req("address")} name="address" value={formData.address} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Age{star("age")}</label>
        <input required={req("age")} type="number" name="age" value={formData.age} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Date of Birth{star("dob")}</label>
        <input required={req("dob")} type="date" name="dob" value={formData.dob} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div className="col-span-2">
        <label className="text-xs text-text_secondary mb-1 block">Gender</label>
        <div className="flex gap-4 mt-2">
          <label className="flex items-center gap-2 text-text_primary"><input type="radio" name="gender" value="Male" checked={formData.gender==='Male'} onChange={handleChange} className="accent-primary"/> Male</label>
          <label className="flex items-center gap-2 text-text_primary"><input type="radio" name="gender" value="Female" checked={formData.gender==='Female'} onChange={handleChange} className="accent-primary"/> Female</label>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Home Phone{star("homePhone")}</label>
        <input required={req("homePhone")} name="homePhone" value={formData.homePhone} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Cell Phone *</label>
        <input required name="cellPhone" value={formData.cellPhone} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">E-mail Id{star("email")}</label>
        <input required={req("email")} type="email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Place of Work / Company Name{star("company")}</label>
        <input required={req("company")} name="company" value={formData.company} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Job Title{star("designation")}</label>
        <input required={req("designation")} name="designation" value={formData.designation} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
    </div>
  </section>
  );
};

const PhysicalDetails = ({ formData, handleChange, req }) => {
  const star = (k) => (req(k) ? " *" : "");
  return (
  <section>
    <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Physical Details & Emergency</h2>
    <div className="grid grid-cols-3 gap-4 mb-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Height (cm){star("height")}</label>
        <input required={req("height")} type="number" name="height" value={formData.height} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Weight (kg){star("weight")}</label>
        <input required={req("weight")} type="number" name="weight" value={formData.weight} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block flex items-center gap-1"><Calculator size={12}/> Auto BMI</label>
        <input readOnly value={formData.bmi} className="w-full bg-main_bg border border-border_soft/30 rounded p-2 text-gold font-bold cursor-not-allowed" placeholder="Calculated" />
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Emergency Contact Name{star("emergencyName")}</label>
        <input required={req("emergencyName")} name="emergencyName" value={formData.emergencyName} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Emergency Phone Number{star("emergencyPhone")}</label>
        <input required={req("emergencyPhone")} name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
    </div>
  </section>
  );
};

const AdditionalInfo = ({ fields, values, onChange }) => {
  if (!fields.length) return null;
  return (
    <section data-testid="additional-info-section">
      <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Additional Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fields.map((f) => (
          <div key={f.id}>
            <label className="text-xs text-text_secondary mb-1 block">{f.label}{f.required ? " *" : ""}</label>
            <input required={f.required} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} data-testid={`custom-field-${f.key}`} value={values[f.key] || ""} onChange={(e) => onChange(f.key, e.target.value)} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
          </div>
        ))}
      </div>
    </section>
  );
};

const MembershipDetails = ({ formData, handleChange, packages, branches, toggleAddon, branchLocked }) => {
  const memberships = packages.filter(p => (p.type || "Membership") !== "Add-on");
  const addons = packages.filter(p => (p.type || "Membership") === "Add-on");
  return (
  <section>
    <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Membership Details</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">How did you hear about us?</label>
        <select name="heardAbout" value={formData.heardAbout} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary">
          <option>Newspaper Ad</option>
          <option>Internet / Web Enquiry</option>
          <option>Social Media</option>
          <option>Word of Mouth</option>
          <option>Other</option>
        </select>
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Membership Package *</label>
        <select required name="package" value={formData.package} onChange={handleChange} data-testid="member-package-select" className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary">
          {memberships.length === 0 && <option value="">Loading packages...</option>}
          {memberships.map(pkg => (
            <option key={pkg.id} value={pkg.name}>{pkg.name} (₹{pkg.price})</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Branch *</label>
        <select required name="branch" value={formData.branch} onChange={handleChange} disabled={branchLocked} data-testid="member-branch-select" className={`w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary ${branchLocked ? "opacity-70 cursor-not-allowed" : ""}`}>
          {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Start Date *</label>
        <input required type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Discount</label>
        <input type="number" min="0" name="discount" value={formData.discount} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Discount Type</label>
        <select name="discountType" value={formData.discountType} onChange={handleChange} className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary">
          <option value="percent">Percent (%)</option>
          <option value="flat">Flat (Rupees)</option>
        </select>
      </div>
    </div>
    {addons.length > 0 && (
      <div className="mt-2">
        <label className="text-xs text-text_secondary mb-2 block">Add-on Packages (Transformation / Personal Training)</label>
        <div className="flex flex-wrap gap-2">
          {addons.map(a => {
            const sel = formData.addons.includes(a.name);
            return (
              <button type="button" key={a.id} data-testid={`addon-${a.name}`} onClick={() => toggleAddon(a.name)} className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${sel ? "bg-gold text-[#111827] border-gold" : "bg-card_bg text-text_secondary border-border_soft"}`}>{a.name} · ₹{a.price}</button>
            );
          })}
        </div>
      </div>
    )}
  </section>
  );
};

const PaymentSection = ({ formData, handleChange, onUpload, uploading }) => (
  <section>
    <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Payment Collection</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Payment Mode</label>
        <select name="paymentMode" value={formData.paymentMode} onChange={handleChange} data-testid="payment-mode" className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary">
          {["Cash", "UPI", "Card", "Net Banking"].map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Amount Collected (₹)</label>
        <input type="number" min="0" name="paymentAmount" value={formData.paymentAmount} onChange={handleChange} data-testid="payment-amount" className="w-full bg-card_bg border border-border_soft rounded p-2 text-text_primary" />
      </div>
      <div>
        <label className="text-xs text-text_secondary mb-1 block">Payment Proof (screenshot / receipt)</label>
        <input type="file" accept="image/*,application/pdf" onChange={onUpload} data-testid="payment-proof-upload" className="w-full text-xs text-text_secondary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-[#111827] file:font-bold file:cursor-pointer" />
        {uploading && <p className="text-xs text-gold mt-1">Uploading...</p>}
        {formData.paymentProof && !uploading && <p className="text-xs text-accent_success mt-1">✓ Proof uploaded</p>}
      </div>
    </div>
  </section>
);

const AgreementSection = ({ agreement, formData, setFormData }) => (
  <section>
    <h2 className="text-lg font-bold text-gold mb-4 border-b border-border_soft/30 pb-2">Membership Agreement & Signature</h2>
    <div className="bg-card_bg p-4 rounded-lg mb-4 h-44 overflow-y-auto border border-border_soft/50 text-xs text-text_secondary leading-relaxed">
      <strong className="text-text_primary mb-2 block">{agreement.title}</strong>
      <ol className="list-decimal list-inside space-y-1.5">
        {agreement.clauses.map((c, i) => <li key={i}>{c}</li>)}
      </ol>
    </div>
    <label className="flex items-start gap-2 text-sm text-text_primary mb-5 cursor-pointer">
      <input type="checkbox" required data-testid="agree-checkbox" checked={formData.agree} onChange={e => setFormData(f => ({ ...f, agree: e.target.checked }))} className="accent-primary mt-0.5" />
      <span>I have read and agree to the SVT Golden Fitness membership terms and conditions above.</span>
    </label>
    <div className="mb-6">
      <label className="text-xs text-text_secondary mb-2 block">Applicant Signature (Draw below) *</label>
      <SignaturePad onEnd={(data) => setFormData(f => ({...f, signature: data}))} />
    </div>
  </section>
);

export default function MemberSignup() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [branches, setBranches] = useState([]);
  const [agreement, setAgreement] = useState({ title: "Membership Agreement", clauses: [] });
  const [user, setUser] = useState(null);
  const [fieldConfig, setFieldConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "", middleName: "", lastName: "",
    address: "", age: "", dob: "", gender: "Male",
    homePhone: "", cellPhone: "", email: "",
    company: "", designation: "",
    emergencyName: "", emergencyPhone: "",
    heardAbout: "Internet",
    package: "", branch: "", startDate: new Date().toISOString().split('T')[0], discount: 0, discountType: "percent",
    addons: [],
    paymentMode: "Cash", paymentAmount: 0, paymentProof: "",
    height: "", weight: "", bmi: "",
    signature: "", agree: false,
    customFields: {},
  });

  const fetchInit = useCallback(async () => {
    try {
      const [pr, br, ag, me, mf] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/packages`),
        axios.get(`${BACKEND_URL}/api/branches`),
        axios.get(`${BACKEND_URL}/api/agreement`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
        axios.get(`${BACKEND_URL}/api/member-fields`),
      ]);
      setPackages(pr.data);
      setBranches(br.data);
      setAgreement(ag.data);
      setFieldConfig(mf.data);
      const u = me.data.user;
      setUser(u);
      const scoped = rank(u.role) < 80;   // below GM => pinned to own branch
      const memberships = pr.data.filter(p => (p.type || "Membership") !== "Add-on");
      setFormData(f => ({
        ...f,
        package: memberships[0]?.name || "",
        branch: scoped && u.branch ? u.branch : (br.data[0]?.name || "Main"),
      }));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  const requiredMap = {};
  fieldConfig.forEach(f => { if (f.builtin) requiredMap[f.key] = f.required; });
  const req = (key) => !!requiredMap[key];
  const customFieldDefs = fieldConfig.filter(f => !f.builtin);
  const setCustomField = (key, value) => setFormData(f => ({ ...f, customFields: { ...f.customFields, [key]: value } }));

  const toggleAddon = (name) => setFormData(f => ({ ...f, addons: f.addons.includes(name) ? f.addons.filter(x => x !== name) : [...f.addons, name] }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${BACKEND_URL}/api/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFormData(f => ({ ...f, paymentProof: res.data.path }));
    } catch (err) { alert("Upload failed. Please try again."); }
    setUploading(false);
  };

  useEffect(() => {
    if (formData.height && formData.weight) {
      const h = parseFloat(formData.height) / 100;
      const w = parseFloat(formData.weight);
      if (h > 0 && w > 0) {
        const bmi = (w / (h * h)).toFixed(1);
        if (formData.bmi !== bmi) {
            setFormData(f => ({ ...f, bmi }));
        }
      }
    }
  }, [formData.height, formData.weight, formData.bmi]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agree) { alert("Please accept the membership agreement to continue."); return; }
    setLoading(true);
    const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ").trim();
    
    const payload = {
      name: fullName,
      package: formData.package,
      phone: formData.cellPhone,
      cellPhone: formData.cellPhone,
      homePhone: formData.homePhone,
      email: formData.email,
      address: formData.address,
      dob: formData.dob,
      age: formData.age,
      gender: formData.gender,
      height: formData.height,
      weight: formData.weight,
      bmi: formData.bmi,
      emergency_name: formData.emergencyName,
      emergency_phone: formData.emergencyPhone,
      company: formData.company,
      designation: formData.designation,
      heard_about: formData.heardAbout,
      start_date: formData.startDate,
      branch: formData.branch,
      addons: formData.addons,
      discount: parseFloat(formData.discount) || 0,
      discount_type: formData.discountType,
      payment_mode: formData.paymentMode,
      payment_amount: parseFloat(formData.paymentAmount) || 0,
      payment_proof: formData.paymentProof,
      agreement_accepted: formData.agree,
      custom_fields: formData.customFields || {},
      signature: formData.signature
    };

    try {
      await axios.post(`${BACKEND_URL}/api/members`, payload);
      navigate("/admin/members");
    } catch (err) {
      alert(err.response?.data?.detail || "Could not create member.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => navigate(-1)} className="text-text_secondary hover:text-text_primary flex items-center gap-2 transition-colors">
          <ArrowLeft size={20}/> Back
        </button>
        <h1 className="text-3xl font-serif text-text_primary">SVT Golden Fitness Signup</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 bg-card_bg border border-border_soft p-8 rounded-2xl shadow-xl">
        <PersonalDetails formData={formData} handleChange={handleChange} req={req} />
        <PhysicalDetails formData={formData} handleChange={handleChange} req={req} />
        <AdditionalInfo fields={customFieldDefs} values={formData.customFields} onChange={setCustomField} />
        <MembershipDetails formData={formData} handleChange={handleChange} packages={packages} branches={branches} toggleAddon={toggleAddon} branchLocked={user && rank(user.role) < 80} />
        <PaymentSection formData={formData} handleChange={handleChange} onUpload={handleUpload} uploading={uploading} />
        <AgreementSection agreement={agreement} formData={formData} setFormData={setFormData} />

        <button type="submit" disabled={loading} className="w-full bg-primary text-[#111827] font-bold py-4 rounded-full flex justify-center items-center gap-2 hover:bg-primary_hover transition-colors disabled:opacity-50 text-lg shadow-md">
          {loading ? "Processing..." : <><Save size={24}/> Finalize Registration & Auto-Invoice</>}
        </button>
      </form>
    </div>
  );
}
